import React from "react"
import { useSearchParams } from "react-router-dom"
import { Container, Row, Col, Tooltip } from "reactstrap"
import Skeleton from "react-loading-skeleton"
import "react-loading-skeleton/dist/skeleton.css"
import "../../assets/scss/app.scss"
import "./CreativeInsightsDashboard.css"
import SearchProgressModal from "../../components/Common/Modal/SearchProgressModal"
import KeywordTrackingDetailsModal from "../../components/Common/Modal/KeywordTrackingDetailsModal"
import AppsTrackingPlanModal from "../../components/Common/Modal/AppsTrackingPlanModal"
import fallbackLogo from "../../assets/images/new_web_logo.png"
import PlatformTabs from "../../components/Common/PlatformTabs"
import RecentApps from "../../components/Common/RecentApps"
import SEO from "../../components/Common/SEO"
import { PAGE_SEO } from "../../config/seoConfig"
import { generateCanonicalUrl } from "../../utils/seoUtils"
import { blogApiUrl } from "../../utils/variables"
import { buildAdLibraryUrl } from "../../utils/facebookAdsLibrary"
import { fetchRecentApps } from "../../utils/recentAppsApi"
import { useAnalytics, ANALYTICS_EVENTS } from "../../analytics"

// Static filter options (do not depend on API response)
// Based on actual API response format
const AD_FORMAT_OPTIONS = ["All", "image", "video"]
const CTA_TYPE_OPTIONS = ["All", "unknown", "install_now", "learn_more", "join_free", "play_today"]
const PLATFORM_OPTIONS = ["All", "FACEBOOK", "INSTAGRAM", "AUDIENCE_NETWORK", "MESSENGER", "THREADS"]

const TEMPLATE_AD_OPTIONS = ["All", "template", "regular"]

// Multi-select dropdown component that matches existing filter-select styling
const MultiSelectFilter = ({ options, selected, onToggle, placeholder, formatValue }) => {
    const [isOpen, setIsOpen] = React.useState(false)
    const dropdownRef = React.useRef(null)

    React.useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setIsOpen(false)
            }
        }
        document.addEventListener("mousedown", handleClickOutside)
        return () => document.removeEventListener("mousedown", handleClickOutside)
    }, [])

    const displayText = !selected || selected.length === 0
        ? placeholder
        : selected.length === 1
            ? formatValue(selected[0])
            : placeholder

    return (
        <div ref={dropdownRef} style={{ position: "relative", width: "100%" }}>
            <button
                type="button"
                className="filter-select"
                onClick={() => setIsOpen(prev => !prev)}
                style={{
                    width: "100%",
                    textAlign: "left",
                    cursor: "pointer",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    padding: "6px 10px",
                    // Let the base .filter-select class control background, border, color, and font-size
                    borderRadius: "8px"
                }}
            >
                <span>{displayText}</span>
                <span style={{ marginLeft: 8, fontSize: 10 }}>{isOpen ? "▲" : "▼"}</span>
            </button>
            {isOpen && (
                <div
                    style={{
                        position: "absolute",
                        top: "100%",
                        left: 0,
                        right: 0,
                        marginTop: 4,
                        // Match inner field background & border like regular filters
                        background: "#05060a",
                        border: "1px solid rgba(255, 255, 255, 0.08)",
                        borderRadius: 8,
                        zIndex: 1000,
                        maxHeight: 220,
                        overflowY: "auto",
                        boxShadow: "0 4px 12px rgba(0, 0, 0, 0.3)",
                        padding: "8px 0 14px 6px",
                        display: "flex",
                        flexDirection: "column",
                        gap: 12
                    }}
                >
                    {options.filter(opt => opt !== "All").map(opt => {
                        const isSelected = Array.isArray(selected) && selected.includes(opt)
                        return (
                            <label
                                key={opt}
                                style={{
                                    cursor: "pointer",
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 12,
                                    fontSize: 15,
                                    color: "#dbe7f0",
                                    // Match inner field background color like regular filters (e.g., Verification status)
                                    background: "#05060a"
                                }}
                            >
                                <input
                                    type="checkbox"
                                    checked={isSelected}
                                    onChange={() => onToggle(opt)}
                                    style={{
                                        width: 18,
                                        height: 18,
                                        margin: 0,
                                        cursor: "pointer",
                                        accentColor: "#F6255A"
                                    }}
                                />
                                <span>{formatValue(opt)}</span>
                            </label>
                        )
                    })}
                </div>
            )}
        </div>
    )
}

// Single-select dropdown component styled like MultiSelectFilter, but with a single value
const SingleSelectFilter = ({ options, value, onChange, placeholder, formatValue }) => {
    const [isOpen, setIsOpen] = React.useState(false)
    const dropdownRef = React.useRef(null)
    const [tooltipOpen, setTooltipOpen] = React.useState({})

    const toggleTooltip = (val) => {
        setTooltipOpen(prev => ({ ...prev, [val]: !prev[val] }))
    }

    React.useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setIsOpen(false)
            }
        }
        document.addEventListener("mousedown", handleClickOutside)
        return () => document.removeEventListener("mousedown", handleClickOutside)
    }, [])

    const currentOption = options.find(opt => opt.value === value)
    // When the selected value is "All", show the placeholder text (e.g. "Verification status")
    // so the closed dropdown looks like a label. Inside the dropdown, the first option
    // label can still be "All".
    const displayText =
        value === "All" && placeholder
            ? placeholder
            : currentOption
                ? currentOption.label
                : placeholder

    const handleSelect = (val) => {
        onChange(val)
        setIsOpen(false)
    }

    return (
        <div ref={dropdownRef} style={{ position: "relative", width: "100%" }}>
            <button
                type="button"
                className="filter-select"
                onClick={() => setIsOpen(prev => !prev)}
                style={{
                    width: "100%",
                    textAlign: "left",
                    cursor: "pointer",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    padding: "6px 10px",
                    // Let the base .filter-select class control background, border, color, and font-size
                    borderRadius: "8px"
                }}
            >
                <span>{displayText}</span>
                <span style={{ marginLeft: 8, fontSize: 10 }}>{isOpen ? "▲" : "▼"}</span>
            </button>
            {isOpen && (
                <div
                    style={{
                        position: "absolute",
                        top: "100%",
                        left: 0,
                        right: 0,
                        marginTop: 4,
                        // Match inner field background & border like regular filters
                        background: "#05060a",
                        border: "1px solid rgba(255, 255, 255, 0.08)",
                        borderRadius: 8,
                        zIndex: 1000,
                        maxHeight: 220,
                        overflowY: "auto",
                        boxShadow: "0 4px 12px rgba(0, 0, 0, 0.3)",
                        padding: "8px 0 14px 6px",
                        display: "flex",
                        flexDirection: "column",
                        gap: 12
                    }}
                >
                    {options.map((opt, i) => (
                        <div key={opt.value} style={{ display: "flex", alignItems: "center", gap: 8, paddingRight: 10 }}>
                            <button
                                type="button"
                                onClick={() => handleSelect(opt.value)}
                                style={{
                                    cursor: "pointer",
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 12,
                                    fontSize: 15,
                                    color: "#dbe7f0",
                                    background: "transparent",
                                    border: "none",
                                    padding: "2px 0 2px 0",
                                    textAlign: "left",
                                    flex: 1
                                }}
                            >
                                <span>{formatValue ? formatValue(opt.label) : opt.label}</span>
                            </button>
                            {opt.tooltip && (
                                <>
                                    <span
                                        id={`tooltip-${opt.value.replace(/[^a-zA-Z0-9]/g, '')}-${i}`}
                                        style={{
                                            color: "#888",
                                            cursor: "help",
                                            display: "inline-flex",
                                            alignItems: "center",
                                            justifyContent: "center",
                                            width: 16,
                                            height: 16,
                                            borderRadius: "50%",
                                            border: "1px solid #888",
                                            fontSize: 10,
                                            flexShrink: 0
                                        }}
                                    >
                                        i
                                    </span>
                                    <Tooltip
                                        placement="right"
                                        isOpen={tooltipOpen[opt.value] || false}
                                        target={`tooltip-${opt.value.replace(/[^a-zA-Z0-9]/g, '')}-${i}`}
                                        toggle={() => toggleTooltip(opt.value)}
                                        modifiers={[
                                            {
                                                name: 'flip',
                                                options: {
                                                    fallbackPlacements: ['left', 'bottom'],
                                                },
                                            },
                                            {
                                                name: 'preventOverflow',
                                                options: {
                                                    boundary: 'window',
                                                },
                                            }
                                        ]}
                                    >
                                        {opt.tooltip}
                                    </Tooltip>
                                </>
                            )}
                        </div>
                    ))}
                </div>
            )}
        </div>
    )
}

const CreativeInsightsDashboard = () => {
    const { trackEvent } = useAnalytics()
    const [searchParams, setSearchParams] = useSearchParams()
    const [searchInput, setSearchInput] = React.useState("")
    const [query, setQuery] = React.useState("")
    // The selected app's id, when the search came from picking a specific
    // app (the search modal or a "Recently Searched" card) rather than a
    // bare keyword typed and submitted directly - sent as `app_id` on the
    // ads search request so the backend can match the exact app rather than
    // a name-based guess.
    const [searchedAppId, setSearchedAppId] = React.useState(null)
    const [ads, setAds] = React.useState([])
    const [loading, setLoading] = React.useState(false)
    const [error, setError] = React.useState(null)
    const [page, setPage] = React.useState(1)
    const [totalPages, setTotalPages] = React.useState(1)
    const [filtering, setFiltering] = React.useState(false)
    // Meta / Google tabs (like keyword explorer)
    const [selectedEngine, setSelectedEngine] = React.useState("meta")
    // Creative-pattern filters (driven by API fields)
    const [adFormatFilter, setAdFormatFilter] = React.useState([])
    const [ctaTypeFilter, setCtaTypeFilter] = React.useState([])
    const [platformFilter, setPlatformFilter] = React.useState([])
    const [dateRange, setDateRange] = React.useState("all")
    const [showNewlyAdded, setShowNewlyAdded] = React.useState(false)
    const [sortBy, setSortBy] = React.useState("Imp. Estimate")
    const [viewMode, setViewMode] = React.useState("grid")
    const [isBackendProcessing, setIsBackendProcessing] = React.useState(false)
    const [noResults, setNoResults] = React.useState(false)
    const [hasSearched, setHasSearched] = React.useState(false)
    // Verification status filter - default to fully_verified for new searches
    const [verificationStatusFilter, setVerificationStatusFilter] = React.useState("fully_verified")
    const [hasUserTouchedVerificationFilter, setHasUserTouchedVerificationFilter] = React.useState(false)
    const [activeStatusFilter, setActiveStatusFilter] = React.useState("All")
    // Ad Discovery's ad-creative search only has data for Meta today — Steam and
    // Apple Vision are shown (matching the shared platform tabs UI everywhere
    // else) but disabled with a "Coming soon" tooltip until the API supports them.
    const [activePlatformTab, setActivePlatformTab] = React.useState("meta")
    const [templateAdFilter, setTemplateAdFilter] = React.useState([])
    const [showAppSearchModal, setShowAppSearchModal] = React.useState(false)
    const [modalSearchTerm, setModalSearchTerm] = React.useState("")
    const [showUpgradeModal, setShowUpgradeModal] = React.useState(false)
    const [showDateRangeModal, setShowDateRangeModal] = React.useState(false)
    const [customStartDate, setCustomStartDate] = React.useState("")
    const [customEndDate, setCustomEndDate] = React.useState("")
    const [dateRangeError, setDateRangeError] = React.useState("")
    const [refreshKey, setRefreshKey] = React.useState(0)
    // "Recently Searched" panel, shown before a search has been made - same
    // shared component/util UGC Explorer uses (src/components/Common/RecentApps.jsx,
    // src/utils/recentAppsApi.js), pointed at the ads backend's own list
    // (`facebook_ads` feature, `/ads/api/recent-apps/`, on `blogApiUrl`
    // rather than the okara host the other features live on).
    const [recentApps, setRecentApps] = React.useState([])
    const [loadingRecentApps, setLoadingRecentApps] = React.useState(true)
    const [showSearchProgressModal, setShowSearchProgressModal] = React.useState(false)
    const [isPolling, setIsPolling] = React.useState(false)
    const [searchProgress, setSearchProgress] = React.useState(0)
    const progressIntervalRef = React.useRef(null)
    // Refs that mirror isPolling and searchProgress so async fetchAds closures
    // always read the current value and never get a stale snapshot.
    const isPollingRef = React.useRef(false)
    const searchProgressRef = React.useRef(0)

    const getSortKey = label => {
        switch (label) {
            case "Pertinence":
                return "pertinence"
            case "First Discovered":
                return "first_discovered"
            case "Ad Duration":
                return "ad_duration"
            case "Views":
                return "views"
            case "Likes":
                return "likes"
            case "Comments":
                return "comments"
            case "Reposts":
                return "reposts"
            case "Imp. Estimate":
            default:
                return "impressions_estimate"
        }
    }

    React.useEffect(() => {
        document.title = "Ad Discovery | Keyquest"
    }, [])

    // "Recently Searched" - loaded once on mount, same as UGC Explorer does
    // for its own recent-apps panel; nothing here depends on any filter, so
    // there's nothing to re-fetch on.
    React.useEffect(() => {
        let cancelled = false
        ;(async () => {
            setLoadingRecentApps(true)
            try {
                const result = await fetchRecentApps("facebook_ads", { limit: 10, baseUrl: blogApiUrl })
                if (!cancelled) setRecentApps(result?.recent_apps || [])
            } catch (err) {
                console.error("Error fetching Ad Discovery recent apps:", err)
            } finally {
                if (!cancelled) setLoadingRecentApps(false)
            }
        })()
        return () => { cancelled = true }
    }, [])

    // Restore search state from sessionStorage on mount (similar to keyword explorer)
    React.useEffect(() => {
        // Deep link from an app's dashboard (?app_name=...&app_id=...): search
        // that app instead of restoring the last session, then drop the params
        // so later searches/refreshes aren't pinned to it.
        const deepLinkName = searchParams.get("app_name")
        if (deepLinkName && deepLinkName.trim()) {
            handleSelectRecentApp({ name: deepLinkName, app_id: searchParams.get("app_id") })
            setSearchParams({}, { replace: true })
            return
        }
        try {
            const saved = sessionStorage.getItem('creativeInsightsLastResponse')
            if (!saved) return

            const parsed = JSON.parse(saved)
            if (!parsed || !parsed.query) return

            // Only restore if we don't already have a query (avoid clobbering active search)
            if (query && query.trim()) return

            // Restore search state
            if (parsed.searchInput) {
                setSearchInput(parsed.searchInput)
            }
            if (parsed.query) {
                setQuery(parsed.query)
            }
            if (parsed.hasSearched !== undefined) {
                setHasSearched(parsed.hasSearched)
            }
            if (parsed.page) {
                setPage(parsed.page)
            }
            if (parsed.ads && Array.isArray(parsed.ads)) {
                setAds(parsed.ads)
            }
            if (parsed.totalPages) {
                setTotalPages(parsed.totalPages)
            }
            // Restore filters
            if (parsed.adFormatFilter) {
                setAdFormatFilter(parsed.adFormatFilter)
            }
            if (parsed.hookTypeFilter) {
                // Hook type filter removed - no longer supported by backend
            }
            if (parsed.ctaTypeFilter) {
                setCtaTypeFilter(parsed.ctaTypeFilter)
            }
            if (parsed.gameTagFilter) {
                // Game tag filter removed - no longer supported by backend
            }
            if (parsed.platformFilter) {
                setPlatformFilter(parsed.platformFilter)
            }
            if (parsed.verificationStatusFilter) {
                setVerificationStatusFilter(parsed.verificationStatusFilter)
            }
            if (parsed.hasUserTouchedVerificationFilter !== undefined) {
                setHasUserTouchedVerificationFilter(parsed.hasUserTouchedVerificationFilter)
            }
            if (parsed.activeStatusFilter) {
                setActiveStatusFilter(parsed.activeStatusFilter)
            }
            if (parsed.templateAdFilter) {
                setTemplateAdFilter(parsed.templateAdFilter)
            }
            if (parsed.dateRange) {
                setDateRange(parsed.dateRange)
            }
            if (parsed.showNewlyAdded !== undefined) {
                setShowNewlyAdded(parsed.showNewlyAdded)
            }
            if (parsed.sortBy) {
                setSortBy(parsed.sortBy)
            }
            if (parsed.engine) {
                setSelectedEngine(parsed.engine)
            }
        } catch (e) {
            // ignore parse errors
        }
    }, []) // Only run on mount

    // Save search state to sessionStorage when results are available (or when search is done, even with no results)
    React.useEffect(() => {
        try {
            if (query && query.trim() && hasSearched) {
                // Save state even if ads array is empty (to preserve "no results" state)
                const payload = {
                    query: query,
                    searchInput: searchInput,
                    hasSearched: hasSearched,
                    page: page,
                    ads: ads || [],
                    totalPages: totalPages,
                    adFormatFilter: adFormatFilter,
                    ctaTypeFilter: ctaTypeFilter,
                    platformFilter: platformFilter,
                    verificationStatusFilter: verificationStatusFilter,
                    hasUserTouchedVerificationFilter,
                    activeStatusFilter: activeStatusFilter,
                    templateAdFilter: templateAdFilter,
                    dateRange: dateRange,
                    showNewlyAdded: showNewlyAdded,
                    sortBy: sortBy,
                    engine: selectedEngine,
                    savedAt: Date.now()
                }
                sessionStorage.setItem('creativeInsightsLastResponse', JSON.stringify(payload))
            } else if (!query || !query.trim()) {
                // Clear saved state when search is cleared
                sessionStorage.removeItem('creativeInsightsLastResponse')
            }
        } catch (e) {
            // ignore storage errors
        }
    }, [query, searchInput, hasSearched, page, ads, totalPages, adFormatFilter, ctaTypeFilter, platformFilter, verificationStatusFilter, hasUserTouchedVerificationFilter, activeStatusFilter, templateAdFilter, dateRange, showNewlyAdded, sortBy, selectedEngine])

    // When the search input is cleared, fall back to base search (no query param)
    React.useEffect(() => {
        if (!searchInput.trim() && query !== "") {
            setPage(1)
            setQuery("")
        }
    }, [searchInput, query])

    const handleSearch = () => {
        const trimmed = searchInput.trim()
        if (!trimmed) return

        // If this search term has already been searched (query matches searchInput),
        // refresh the API call but don't open the modal again
        if (query && query.trim() === trimmed) {
            // Already searched this term - trigger API refresh by incrementing refreshKey
            setPage(1)
            setRefreshKey(prev => prev + 1) // This will trigger the useEffect to call the API again
            return
        }

        // New search term - open modal to select app
        setModalSearchTerm(trimmed)
        setShowAppSearchModal(true)
    }

    const handlePrevPage = () => {
        setPage(prev => {
            const next = Math.max(1, prev - 1)
            trackEvent(ANALYTICS_EVENTS.FEATURE.AD_DISCOVERY_PAGINATED, { page: next })
            return next
        })
    }

    const handleNextPage = () => {
        setPage(prev => {
            const maxPages = totalPages
            const next = Math.min(maxPages, prev + 1)
            trackEvent(ANALYTICS_EVENTS.FEATURE.AD_DISCOVERY_PAGINATED, { page: next })
            return next
        })
    }

    const getPageNumbers = (currentPage, totalPagesValue) => {
        const pages = []
        for (let i = 1; i <= Math.min(3, totalPagesValue); i++) {
            pages.push(i)
        }
        if (totalPagesValue > 3) {
            if (currentPage > 3) {
                pages.push("...")
                pages.push(currentPage)
            } else {
                pages.push("...")
            }
        }
        return pages
    }

    const handleDateChange = value => {
        setDateRange(value)
        setPage(1)
        trackEvent(ANALYTICS_EVENTS.FEATURE.AD_DISCOVERY_FILTER_APPLIED, { filter_type: "date_range", value })
    }

    const handleSortChange = event => {
        const value = event.target.value
        setSortBy(value)
        setPage(1)
        trackEvent(ANALYTICS_EVENTS.FEATURE.AD_DISCOVERY_FILTER_APPLIED, { filter_type: "sort", value })
    }

    const handleViewChange = mode => {
        setViewMode(mode)
        trackEvent(ANALYTICS_EVENTS.FEATURE.AD_DISCOVERY_FILTER_APPLIED, { filter_type: "view", value: mode })
    }

    const handleAdFormatToggle = value => {
        setAdFormatFilter(prev => {
            if (Array.isArray(prev) && prev.includes(value)) {
                return prev.filter(v => v !== value)
            }
            return [...(Array.isArray(prev) ? prev : []), value]
        })
        setPage(1)
        trackEvent(ANALYTICS_EVENTS.FEATURE.AD_DISCOVERY_FILTER_APPLIED, { filter_type: "ad_format", value })
    }

    const handleCtaTypeToggle = value => {
        setCtaTypeFilter(prev => {
            if (Array.isArray(prev) && prev.includes(value)) {
                return prev.filter(v => v !== value)
            }
            return [...(Array.isArray(prev) ? prev : []), value]
        })
        setPage(1)
        trackEvent(ANALYTICS_EVENTS.FEATURE.AD_DISCOVERY_FILTER_APPLIED, { filter_type: "cta_type", value })
    }

    const handlePlatformToggle = value => {
        setPlatformFilter(prev => {
            if (Array.isArray(prev) && prev.includes(value)) {
                return prev.filter(v => v !== value)
            }
            return [...(Array.isArray(prev) ? prev : []), value]
        })
        setPage(1)
        trackEvent(ANALYTICS_EVENTS.FEATURE.AD_DISCOVERY_FILTER_APPLIED, { filter_type: "platform", value })
    }

    const handleVerificationStatusChange = value => {
        setHasUserTouchedVerificationFilter(true)
        setVerificationStatusFilter(value)
        setPage(1)
        trackEvent(ANALYTICS_EVENTS.FEATURE.AD_DISCOVERY_FILTER_APPLIED, { filter_type: "verification_status", value })
    }

    const handleActiveStatusChange = value => {
        setActiveStatusFilter(value)
        setPage(1)
        trackEvent(ANALYTICS_EVENTS.FEATURE.AD_DISCOVERY_FILTER_APPLIED, { filter_type: "active_status", value })
    }

    const handleTemplateAdToggle = value => {
        setTemplateAdFilter(prev => {
            if (Array.isArray(prev) && prev.includes(value)) {
                return prev.filter(v => v !== value)
            }
            return [...(Array.isArray(prev) ? prev : []), value]
        })
        setPage(1)
        trackEvent(ANALYTICS_EVENTS.FEATURE.AD_DISCOVERY_FILTER_APPLIED, { filter_type: "template_ad", value })
    }

    // Clears the search entirely - the input, the active query, and the
    // selected app id - so the fetch effect resets `hasSearched` to false
    // and "Recently Searched" reappears. This is the one function every
    // "clear" affordance on this screen should call (the input's own inline
    // ✕, the toolbar's Clear button, and the applied-search chip's ×) so
    // clearing the search always behaves the same way everywhere.
    const handleClearSearchInput = () => {
        setSearchInput("")
        setSearchedAppId(null)
        // useEffect on searchInput + query and the main fetch effect
        // will take care of clearing results and pagination state
    }

    // When an app is chosen from the modal, use its name as the ads keyword,
    // close the modal, and trigger the ads API via the existing effect on `query`.
    const handleAppSelectedFromModal = (app) => {
        try {
            if (!app) {
                setShowAppSearchModal(false)
                return
            }
            const name =
                app.name ||
                app.title ||
                app.app_name ||
                app.game_name ||
                app.matched_game_name ||
                ""
            const keyword = (name && String(name).trim()) || modalSearchTerm || searchInput
            if (!keyword || !String(keyword).trim()) {
                setShowAppSearchModal(false)
                return
            }
            const trimmed = String(keyword).trim()
            const id = app.item_id || app._id || app.app_id || null
            setSearchInput(trimmed)
            setPage(1)
            setQuery(trimmed)
            setSearchedAppId(id ? String(id) : null)
            // New search: reset verification filter defaults
            setVerificationStatusFilter("fully_verified")
            setHasUserTouchedVerificationFilter(false)
            trackEvent(ANALYTICS_EVENTS.FEATURE.AD_DISCOVERY_SEARCH, { app_id: id, app_name: name })
        } finally {
            setShowAppSearchModal(false)
        }
    }

    // Clicking a "Recently Searched" card re-runs the same search this
    // screen already does by name/keyword, now also carrying the app's id
    // (recent-apps rows always have one - RECENT_APPS_API.md) - same fields
    // handleAppSelectedFromModal sets, just without the search modal.
    const handleSelectRecentApp = (app) => {
        if (!app?.name) return
        const trimmed = String(app.name).trim()
        if (!trimmed) return
        setSearchInput(trimmed)
        setPage(1)
        setQuery(trimmed)
        setSearchedAppId(app.app_id ? String(app.app_id) : null)
        setVerificationStatusFilter("fully_verified")
        setHasUserTouchedVerificationFilter(false)
        const id = app.item_id || app._id || app.app_id || null
        trackEvent(ANALYTICS_EVENTS.FEATURE.AD_DISCOVERY_SEARCH, { app_id: id, app_name: trimmed })
    }

    const handleNewlyAddedChange = checked => {
        setShowNewlyAdded(checked)
        setPage(1)
        trackEvent(ANALYTICS_EVENTS.FEATURE.AD_DISCOVERY_FILTER_APPLIED, { filter_type: "newly_added", value: checked })
    }

    // Helper function to format filter values professionally
    const formatFilterValue = (value) => {
        if (!value) return value

        // Handle special cases
        const specialCases = {
            "ugc": "UGC",
            "cta": "CTA",
            "3d": "3 Days",
            "7d": "7 Days",
            "30d": "30 Days"
        }

        if (specialCases[value.toLowerCase()]) {
            return specialCases[value.toLowerCase()]
        }

        // Convert SNAKE_CASE or kebab-case to Title Case
        if (value.includes("_") || value.includes("-")) {
            return value
                .split(/[_-]/)
                .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
                .join(" ")
        }

        // Convert UPPERCASE to Title Case
        if (value === value.toUpperCase() && value.length > 1) {
            return value.charAt(0).toUpperCase() + value.slice(1).toLowerCase()
        }

        // Capitalize first letter
        return value.charAt(0).toUpperCase() + value.slice(1).toLowerCase()
    }

    // Get all active filters for display
    const getActiveFilters = () => {
        const filters = {}

        if (verificationStatusFilter !== "All") {
            filters.verification_status = verificationStatusFilter === "fully_verified" ? "Fully Verified" : "Partial Match"
        }
        if (activeStatusFilter !== "All") {
            filters.status = activeStatusFilter === "active" ? "Active" : "Inactive"
        }
        if (Array.isArray(platformFilter) && platformFilter.length > 0) {
            filters.platform = platformFilter.map(v => formatFilterValue(v))
        }
        if (Array.isArray(adFormatFilter) && adFormatFilter.length > 0) {
            filters.format = adFormatFilter.map(v => formatFilterValue(v))
        }
        if (Array.isArray(ctaTypeFilter) && ctaTypeFilter.length > 0) {
            filters.cta = ctaTypeFilter.map(v => formatFilterValue(v))
        }
        if (Array.isArray(templateAdFilter) && templateAdFilter.length > 0) {
            filters.template_ad = templateAdFilter.map(v => (v === "template" ? "Template" : "Regular"))
        }
        if (dateRange && dateRange !== "all") {
            if (dateRange === "custom" && customStartDate && customEndDate) {
                filters.date_range = `${customStartDate} to ${customEndDate}`
            } else {
                const dateLabels = {
                    "today": "Today",
                    "yesterday": "Yesterday",
                    "3d": "3 Days",
                    "7d": "7 Days",
                    "30d": "30 Days",
                    "last_year": "Last Year"
                }
                filters.date_range = dateLabels[dateRange] || formatFilterValue(dateRange)
            }
        }
        if (showNewlyAdded) {
            filters.newly_added = "Newly Added"
        }

        return filters
    }

    // Handler to remove individual filter
    const handleRemoveFilter = (key) => {
        switch (key) {
            case "verification_status":
                setVerificationStatusFilter("All")
                break
            case "status":
                setActiveStatusFilter("All")
                break
            case "platform":
                setPlatformFilter([])
                break
            case "format":
                setAdFormatFilter([])
                break
            case "cta":
                setCtaTypeFilter([])
                break
            case "template_ad":
                setTemplateAdFilter([])
                break
            case "date_range":
                setDateRange("all")
                setCustomStartDate("")
                setCustomEndDate("")
                break
            case "newly_added":
                setShowNewlyAdded(false)
                break
            default:
                break
        }
        setPage(1)
        trackEvent(ANALYTICS_EVENTS.FEATURE.AD_DISCOVERY_FILTER_REMOVED, { filter_key: key })
    }

    const handleOpenCustomDateRange = () => {
        setShowDateRangeModal(true)
        setDateRangeError("")
    }

    const handleApplyCustomDateRange = () => {
        const start = customStartDate && String(customStartDate).trim()
        const end = customEndDate && String(customEndDate).trim()
        if (!start || !end) {
            setDateRangeError("Please select both From and To dates.")
            return
        }
        const startTs = Date.parse(start)
        const endTs = Date.parse(end)
        if (Number.isNaN(startTs) || Number.isNaN(endTs)) {
            setDateRangeError("Invalid date format.")
            return
        }
        if (startTs >= endTs) {
            setDateRangeError("From date must be before To date.")
            return
        }
        setDateRangeError("")
        setDateRange("custom")
        setPage(1)
        setShowDateRangeModal(false)
        trackEvent(ANALYTICS_EVENTS.FEATURE.AD_DISCOVERY_FILTER_APPLIED, {
            filter_type: "date_range",
            value: "custom",
            start: customStartDate,
            end: customEndDate,
        })
    }

    const handleCancelCustomDateRange = () => {
        setShowDateRangeModal(false)
        setDateRangeError("")
    }

    const handleStartDateChange = (value) => {
        setCustomStartDate(value)
        setDateRangeError("")
        // If end date is set and new start date is >= end date, show error
        if (customEndDate && value && value >= customEndDate) {
            setDateRangeError("From date must be before To date.")
        }
    }

    const handleEndDateChange = (value) => {
        setCustomEndDate(value)
        setDateRangeError("")
        // If start date is set and new end date is <= start date, show error
        if (customStartDate && value && value <= customStartDate) {
            setDateRangeError("To date must be after From date.")
        }
    }

    // Static filter options (do not depend on API)
    const adFormatOptions = AD_FORMAT_OPTIONS
    const ctaTypeOptions = CTA_TYPE_OPTIONS
    const platformOptions = PLATFORM_OPTIONS

    // Helper function to get date range based on dateRange filter
    const getDateRange = React.useCallback(() => {
        if (!dateRange || dateRange === "") {
            return null
        }

        const now = new Date()
        const today = new Date(now.getFullYear(), now.getMonth(), now.getDate())
        today.setHours(0, 0, 0, 0)

        let startTs = null
        let endTs = null

        switch (dateRange) {
            case "today":
                // Today only
                startTs = today.getTime()
                endTs = today.getTime() + (24 * 60 * 60 * 1000) - 1
                break
            case "yesterday":
                // Yesterday only
                const yesterday = new Date(today)
                yesterday.setDate(yesterday.getDate() - 1)
                startTs = yesterday.getTime()
                endTs = yesterday.getTime() + (24 * 60 * 60 * 1000) - 1
                break
            case "3d":
                // Last 3 days up to today (inclusive)
                const threeDaysAgo = new Date(today)
                threeDaysAgo.setDate(threeDaysAgo.getDate() - 2) // 3 days including today
                startTs = threeDaysAgo.getTime()
                endTs = today.getTime() + (24 * 60 * 60 * 1000) - 1
                break
            case "7d":
                // Last 7 days up to today (inclusive)
                const sevenDaysAgo = new Date(today)
                sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 6) // 7 days including today
                startTs = sevenDaysAgo.getTime()
                endTs = today.getTime() + (24 * 60 * 60 * 1000) - 1
                break
            case "30d":
                // Last 30 days up to today (inclusive)
                const thirtyDaysAgo = new Date(today)
                thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 29) // 30 days including today
                startTs = thirtyDaysAgo.getTime()
                endTs = today.getTime() + (24 * 60 * 60 * 1000) - 1
                break
            case "last_year":
                // All of 2025
                startTs = new Date(2025, 0, 1).getTime() // Jan 1, 2025 00:00:00
                endTs = new Date(2025, 11, 31, 23, 59, 59, 999).getTime() // Dec 31, 2025 23:59:59.999
                break
            case "custom":
                // Custom date range handled separately
                if (customStartDate && customEndDate) {
                    const fromDate = new Date(customStartDate)
                    fromDate.setHours(0, 0, 0, 0)
                    startTs = fromDate.getTime()
                    const toDate = new Date(customEndDate)
                    toDate.setHours(23, 59, 59, 999)
                    endTs = toDate.getTime()
                }
                break
            default:
                return null
        }

        return { startTs, endTs }
    }, [dateRange, customStartDate, customEndDate])

    React.useEffect(() => {
        const trimmedQuery = query.trim()

        // When there is no search term, do not call the API.
        // Clear current ads and reset state, but keep filters static.
        if (!trimmedQuery) {
            setAds([])
            setTotalPages(1)
            setIsBackendProcessing(false)
            setNoResults(false)
            setHasSearched(false)
            // Force these off explicitly rather than leaving whatever they
            // already were - clearing the search while a request was still
            // in flight aborts it, and the abort path below deliberately
            // skips `setLoading(false)` (it only turns loading off for a
            // request that finished normally, not one that got cancelled,
            // to avoid a stale response flipping loading back off for a
            // newer one). With no query left to search for at all, there is
            // nothing left that could turn loading back on, so it's safe -
            // and necessary - to just force it off here. Otherwise the grid
            // is stuck showing its 8 skeleton cards indefinitely, with no
            // search applied to explain them.
            setLoading(false)
            setFiltering(false)
            return
        }

        const controller = new AbortController()
        let retryTimeout = null
        let pollingInterval = null

        const fetchAds = async () => {
            setLoading(true)
            setError(null)
            setNoResults(false)
            // Reset progress only if this is a new search (not during polling)
            if (!isPollingRef.current) {
                setSearchProgress(0)
                searchProgressRef.current = 0
            }
            try {
                const url = new URL(`${blogApiUrl}/ads/api/search`)

                // Only call API when there is an explicit search term.
                const isSearch = !!trimmedQuery
                if (isSearch) {
                    url.searchParams.set("query", trimmedQuery)
                }

                // The exact app the search came from, when there is one
                // (picked from the search modal or a "Recently Searched"
                // card) - lets the backend match the specific app rather
                // than guessing from the name alone.
                if (searchedAppId) {
                    url.searchParams.set("app_id", searchedAppId)
                }

                url.searchParams.set("page", String(page))
                url.searchParams.set("limit", "20")

                // Map filters to backend API parameters
                
                // Boolean filters
                if (activeStatusFilter !== "All") {
                    url.searchParams.set("is_active", activeStatusFilter === "active" ? "true" : "false")
                }
                
                if (Array.isArray(templateAdFilter) && templateAdFilter.length > 0) {
                    const hasTemplate = templateAdFilter.includes("template")
                    const hasRegular = templateAdFilter.includes("regular")
                    if (hasTemplate && !hasRegular) {
                        url.searchParams.set("has_template_variables", "true")
                    } else if (!hasTemplate && hasRegular) {
                        url.searchParams.set("has_template_variables", "false")
                    }
                }
                
                // Ad format filters - map to backend boolean filters
                if (Array.isArray(adFormatFilter) && adFormatFilter.length > 0) {
                    const hasImage = adFormatFilter.includes("image")
                    const hasVideo = adFormatFilter.includes("video")
                    if (hasImage) {
                        url.searchParams.set("has_images", "true")
                    }
                    if (hasVideo) {
                        url.searchParams.set("has_videos", "true")
                    }
                }
                
                // CTA type filters - map to backend cta_text parameter
                if (Array.isArray(ctaTypeFilter) && ctaTypeFilter.length > 0) {
                    // Filter out "All" and join selected CTA types
                    const selectedCtaTypes = ctaTypeFilter.filter(cta => cta !== "All")
                    if (selectedCtaTypes.length > 0) {
                        url.searchParams.set("cta_text", selectedCtaTypes.join(","))
                    }
                }
                
                // Platform filters
                if (Array.isArray(platformFilter) && platformFilter.length > 0) {
                    url.searchParams.set("platforms", platformFilter.join(","))
                }
                
                // Verification status
                if (verificationStatusFilter !== "All") {
                    const statusMap = {
                        "fully_verified": "FULLY_VERIFIED",
                        "partial": "UNVERIFIED"
                    }
                    if (statusMap[verificationStatusFilter]) {
                        url.searchParams.set("verification_status", statusMap[verificationStatusFilter])
                    }
                }
                
                // Date filters - convert to Unix timestamps
                const dateRangeFilter = getDateRange()
                if (dateRangeFilter && dateRangeFilter.startTs && dateRangeFilter.endTs) {
                    // Convert milliseconds to seconds for Unix timestamp
                    url.searchParams.set("start_date_from", Math.floor(dateRangeFilter.startTs / 1000).toString())
                    url.searchParams.set("start_date_to", Math.floor(dateRangeFilter.endTs / 1000).toString())
                }
                
                // Newly added filter (last 7 days)
                if (showNewlyAdded) {
                    const now = new Date()
                    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate())
                    today.setHours(0, 0, 0, 0)
                    const sevenDaysAgo = new Date(today)
                    sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 6)
                    const thresholdTs = sevenDaysAgo.getTime()
                    url.searchParams.set("date_range_from", Math.floor(thresholdTs / 1000).toString())
                }
                
                // Sorting
                if (sortBy && sortBy !== "") {
                    const sortKey = getSortKey(sortBy)
                    if (sortKey) {
                        url.searchParams.set("sort_by", sortKey)
                        url.searchParams.set("sort_order", "desc")
                    }
                }

                // Bearer token, same as every other authenticated call in
                // this app (`Authorization: Bearer <authToken>`) - so the
                // backend can attribute this search to the signed-in user
                // rather than an anonymous one, same as it already does for
                // every other search-tracking feature.
                const authToken = localStorage.getItem("authToken")
                const res = await fetch(url.toString(), {
                    signal: controller.signal,
                    headers: authToken ? { Authorization: `Bearer ${authToken}` } : {},
                })
                if (!res.ok) {
                    throw new Error(`Failed to fetch ads: ${res.status}`)
                }

                const json = await res.json()

                // Handle new ads_status field for processing state
                if (json && json.ads_status === "processing") {
                    setIsBackendProcessing(true)
                    setShowSearchProgressModal(true)
                    setIsPolling(true)
                    isPollingRef.current = true

                    // Clear any existing progress interval
                    if (progressIntervalRef.current) {
                        clearInterval(progressIntervalRef.current)
                    }

                    // Start smooth progress that will reach 75% over 20 seconds
                    const targetProgress = 75
                    const progressDuration = 20000 // 20 seconds
                    const updateInterval = 1000 // Update every 1 second
                    const totalSteps = progressDuration / updateInterval
                    // Use the ref so recursive calls always calc from the real current progress
                    const progressIncrement = Math.ceil((targetProgress - searchProgressRef.current) / totalSteps)

                    progressIntervalRef.current = setInterval(() => {
                        setSearchProgress(prev => {
                            const newProgress = Math.min(prev + progressIncrement, targetProgress)
                            searchProgressRef.current = newProgress
                            return newProgress
                        })
                    }, updateInterval)

                    // Poll every 20 seconds when status is processing
                    pollingInterval = setTimeout(() => {
                        if (!controller.signal.aborted) {
                            // Clear progress interval before next API call
                            if (progressIntervalRef.current) {
                                clearInterval(progressIntervalRef.current)
                                progressIntervalRef.current = null
                            }
                            fetchAds()
                        }
                    }, 20000)
                    return
                }

                // Handle legacy action field for backward compatibility
                if (json && json.action === "LOADING") {
                    setIsBackendProcessing(true)
                    retryTimeout = setTimeout(() => {
                        if (!controller.signal.aborted) {
                            fetchAds()
                        }
                    }, 5000)
                    return
                }

                // Processing is complete, hide modal and reset polling state
                // Clear progress interval
                if (progressIntervalRef.current) {
                    clearInterval(progressIntervalRef.current)
                    progressIntervalRef.current = null
                }
                setSearchProgress(100)
                searchProgressRef.current = 100
                setTimeout(() => {
                    setShowSearchProgressModal(false)
                }, 500)
                setIsPolling(false)
                isPollingRef.current = false
                setIsBackendProcessing(false)

                const items = Array.isArray(json.data) ? json.data : []
                const pagesFromApi = json?.pagination?.pages

                setAds(items)
                // Drive pagination UI from backend page count
                setTotalPages(pagesFromApi && Number.isFinite(pagesFromApi) && pagesFromApi > 0 ? pagesFromApi : 1)

                // If this was a user search and API returned no data,
                // flag noResults so we can show an empty-state message.
                const isEmpty = !Array.isArray(items) || items.length === 0
                setHasSearched(isSearch)
                setNoResults(isSearch && isEmpty)
            } catch (err) {
                if (err.name === "AbortError") return
                // eslint-disable-next-line no-console
                console.error(err)
                setError("Could not load ads. Please try again.")
                // Clear progress interval on error
                if (progressIntervalRef.current) {
                    clearInterval(progressIntervalRef.current)
                    progressIntervalRef.current = null
                }
                setSearchProgress(0)
                searchProgressRef.current = 0
                setShowSearchProgressModal(false)
                setIsPolling(false)
                isPollingRef.current = false
                setIsBackendProcessing(false)
                setNoResults(false)
            } finally {
                if (!controller.signal.aborted) {
                    setLoading(false)
                }
            }
        }

        fetchAds()

        return () => {
            controller.abort()
            if (retryTimeout) {
                clearTimeout(retryTimeout)
            }
            if (pollingInterval) {
                clearTimeout(pollingInterval)
            }
            if (progressIntervalRef.current) {
                clearInterval(progressIntervalRef.current)
                progressIntervalRef.current = null
            }
            setIsBackendProcessing(false)
            setSearchProgress(0)
            searchProgressRef.current = 0
            setShowSearchProgressModal(false)
            setIsPolling(false)
            isPollingRef.current = false
        }
    }, [query, page, sortBy, refreshKey, activeStatusFilter, getDateRange, platformFilter, showNewlyAdded, templateAdFilter, verificationStatusFilter, adFormatFilter, ctaTypeFilter, searchedAppId])

    // Since backend now handles filtering, we just use the ads directly
    const filteredAds = React.useMemo(() => {
        if (!Array.isArray(ads) || ads.length === 0) {
            return []
        }
        return ads
    }, [ads])

    // Count of fully verified ads in the current backend result set
    const fullyVerifiedCount = React.useMemo(
        () => ads.filter(ad => {
            const status = ad?.verification_status
            return status === "FULLY_VERIFIED" || status === "fully_verified"
        }).length,
        [ads]
    )

    const hasBackendResults = Array.isArray(ads) && ads.length > 0

    // If we're on default "Fully verified" but there are no fully
    // verified ads, automatically fall back to "Partial" – but only
    // until the user manually changes the filter.
    React.useEffect(() => {
        if (
            verificationStatusFilter === "fully_verified" &&
            !hasUserTouchedVerificationFilter &&
            hasBackendResults &&
            fullyVerifiedCount === 0
        ) {
            setVerificationStatusFilter("partial")
        }
    }, [verificationStatusFilter, hasUserTouchedVerificationFilter, hasBackendResults, fullyVerifiedCount])

    const hasFilteredResults = Array.isArray(filteredAds) && filteredAds.length > 0
    const shouldShowFilterEmptyState =
        hasSearched &&
        !noResults &&
        hasBackendResults &&
        !hasFilteredResults

    // Track if any frontend filters are actually active
    const anyFilterActive =
        (Array.isArray(adFormatFilter) && adFormatFilter.length > 0) ||
        (Array.isArray(ctaTypeFilter) && ctaTypeFilter.length > 0) ||
        (Array.isArray(platformFilter) && platformFilter.length > 0) ||
        verificationStatusFilter !== "All" ||
        activeStatusFilter !== "All" ||
        (Array.isArray(templateAdFilter) && templateAdFilter.length > 0) ||
        (dateRange && dateRange !== "all") ||
        showNewlyAdded

    // We only filter the ads from the current backend page on the frontend;
    // pagination (page count) is driven by the backend `pagination.pages`.
    const pagedFilteredAds = hasFilteredResults ? filteredAds : []

    // When filters remove everything, fall back to showing a very small sample (4) of unfiltered ads as suggestions.
    const suggestionAds = shouldShowFilterEmptyState ? ads.slice(0, 4) : []
    const cardsToRender = hasFilteredResults ? pagedFilteredAds : suggestionAds

    // Collect unique page names for the current page (cardsToRender).
    // We only count page names from fully verified ads. For "All" verification status,
    // this still uses only fully verified ads. When the filter is set to "partial",
    // we intentionally hide the domain summary.
    const matchedDomainsForSummary = React.useMemo(() => {
        if (!Array.isArray(cardsToRender) || cardsToRender.length === 0) return []
        const pageMap = new Map() // Use Map to store unique page_name -> page_profile_url
        cardsToRender.forEach(ad => {
            const status = ad?.verification_status
            let normalized = status
            if (status === "FULLY_VERIFIED") normalized = "fully_verified"
            else if (status === "PARTIAL_MATCH") normalized = "partial"
            if (normalized === "fully_verified") {
                const pageName = ad?.page_name
                const pageUrl = ad?.page_profile_url
                if (pageName && typeof pageName === "string" && pageName.trim()) {
                    // Only add if not already in map, or update URL if page_name exists but URL is missing
                    if (!pageMap.has(pageName.trim()) || (pageUrl && !pageMap.get(pageName.trim()))) {
                        pageMap.set(pageName.trim(), pageUrl || "")
                    }
                }
            }
        })
        // Convert Map to array of objects with name and url
        return Array.from(pageMap.entries()).map(([name, url]) => ({ name, url }))
    }, [cardsToRender])

    // Collect unique publishers for the current page (cardsToRender), based on publisher_name.
    // Only include publishers from fully verified ads; hide for partial or mixed results.
    const publishersForSummary = React.useMemo(() => {
        if (!Array.isArray(cardsToRender) || cardsToRender.length === 0) return []
        const names = new Set()
        cardsToRender.forEach(ad => {
            const status = ad?.verification_status
            let normalized = status
            if (status === "FULLY_VERIFIED") normalized = "fully_verified"
            else if (status === "PARTIAL_MATCH") normalized = "partial"
            if (normalized === "fully_verified") {
                const name = ad?.publisher_name
                if (name && typeof name === "string" && name.trim()) {
                    names.add(name.trim())
                }
            }
        })
        return Array.from(names)
    }, [cardsToRender])

    const shouldShowPagination =
        !loading &&
        !error &&
        !shouldShowFilterEmptyState &&
        totalPages > 1

    return (
        <div className="page-content creative-insights-page">
            <SEO
                title={PAGE_SEO.creativeInsights.title}
                description={PAGE_SEO.creativeInsights.description}
                keywords={PAGE_SEO.creativeInsights.keywords}
                canonical={generateCanonicalUrl('/creative-insights')}
                robots={PAGE_SEO.creativeInsights.robots}
            />
            <Container fluid>
                <SearchProgressModal
                    show={showSearchProgressModal}
                    onHide={() => setShowSearchProgressModal(false)}
                    progress={searchProgress}
                    title="Fetching ads"
                    hasResults={ads.length > 0}
                    onCancel={() => {
                        // Stop the progress display
                        setShowSearchProgressModal(false)
                        setIsBackendProcessing(false)
                        setIsPolling(false)
                        setSearchProgress(0)
                        if (progressIntervalRef.current) {
                            clearInterval(progressIntervalRef.current)
                            progressIntervalRef.current = null
                        }
                        // Clear the query so results are not displayed and the fetch useEffect aborts
                        setQuery("")
                        setSearchInput("")
                        setAds([])
                        setHasSearched(false)
                    }}
                />
                <KeywordTrackingDetailsModal
                    show={showAppSearchModal}
                    handleClose={() => setShowAppSearchModal(false)}
                    title="Search Apps & Games"
                    searchTerm={modalSearchTerm}
                    onSearchChange={e => setModalSearchTerm(e.target.value)}
                    onSearchClick={() => { }}
                    results={[]}
                    loading={false}
                    onSelectApp={handleAppSelectedFromModal}
                    showSelectButton={true}
                    enableFallbackPolling={false}
                    // Ad Discovery's ad-creative search only has data for Meta today,
                    // so Steam/Apple Vision are shown disabled (with a tooltip)
                    // rather than searchable here — matches the main platform tabs
                    // above the results.
                    disabledPlatformIds={["steam", "apple_vision"]}
                    disabledPlatformTooltip="Coming soon"
                    onUpgrade={() => {
                        // Close search modal and open upgrade modal when backend
                        // returns { status: 400, action: 'UPGRADE' } with HTTP 200
                        setShowAppSearchModal(false)
                        setShowUpgradeModal(true)
                    }}
                />
                <AppsTrackingPlanModal
                    show={showUpgradeModal}
                    handleClose={() => setShowUpgradeModal(false)}
                    title=" Upgrade your plan to unlock this feature"
                />
                <Row className="mb-3">
                    <Col>
                        <div className="d-flex justify-content-between align-items-start flex-wrap gap-3">
                            <div>
                                <h1 className="creative-page-title">Ad Discovery</h1>
                                <p className="creative-page-subtitle">
                                    Explore ad creatives from leading apps and games. Use filters to discover
                                    winning concepts, formats, and markets that match your strategy.
                                </p>
                            </div>
                        </div>
                    </Col>
                </Row>

                {/* Custom Date Range Modal */}
                {showDateRangeModal && (
                    <div className="creative-date-modal-backdrop">
                        <div className="creative-date-modal">
                            <h4 className="creative-date-modal-title">Custom date range</h4>
                            <p className="creative-date-modal-subtitle">
                                Select a From date (start) and To date (till) to filter ad creatives within that range.
                            </p>
                            <div className="creative-date-modal-body">
                                <div className="creative-date-field">
                                    <label>From</label>
                                    <input
                                        type="date"
                                        value={customStartDate}
                                        max={customEndDate || undefined}
                                        onChange={e => handleStartDateChange(e.target.value)}
                                    />
                                </div>
                                <div className="creative-date-field">
                                    <label>To</label>
                                    <input
                                        type="date"
                                        value={customEndDate}
                                        min={customStartDate || undefined}
                                        onChange={e => handleEndDateChange(e.target.value)}
                                    />
                                </div>
                                {dateRangeError && (
                                    <div className="creative-date-error" style={{ color: "#F6255A", fontSize: "13px", marginTop: "8px", textAlign: "center" }}>
                                        {dateRangeError}
                                    </div>
                                )}
                            </div>
                            <div className="creative-date-modal-footer">
                                <button
                                    type="button"
                                    className="creative-date-btn creative-date-btn-secondary"
                                    onClick={handleCancelCustomDateRange}
                                >
                                    Cancel
                                </button>
                                <button
                                    type="button"
                                    className="creative-date-btn creative-date-btn-primary"
                                    onClick={handleApplyCustomDateRange}
                                    disabled={!customStartDate || !customEndDate || !!dateRangeError}
                                >
                                    Apply
                                </button>
                            </div>
                        </div>
                    </div>
                )}

                {/* Top Search / Filters Strip */}
                <div className="creative-top-search">
                    <div className="top-search-left">
                        <form
                            className="creative-main-searchbar"
                            onSubmit={e => {
                                e.preventDefault()
                                handleSearch()
                            }}
                        >
                            <div className="ca-search-input">
                                <input
                                    type="text"
                                    placeholder="Search here..."
                                    value={searchInput}
                                    style={searchInput ? { paddingRight: '36px' } : {}}
                                    onChange={e => setSearchInput(e.target.value)}
                                />
                                {searchInput && (
                                    <button
                                        type="button"
                                        onClick={handleClearSearchInput}
                                        style={{
                                            position: 'absolute',
                                            right: '12px',
                                            top: '50%',
                                            transform: 'translateY(-50%)',
                                            background: 'none',
                                            border: 'none',
                                            color: '#b0b0b5',
                                            cursor: 'pointer',
                                            padding: '4px',
                                            fontSize: '18px',
                                            lineHeight: '1'
                                        }}
                                        title="Clear search"
                                    >
                                        ✕
                                    </button>
                                )}
                            </div>
                            <button
                                type="submit"
                                className="ca-search-button"
                                aria-label="Search"
                            >
                                Search
                            </button>
                            {/* Separate from the small inline ✕ inside the
                                input - a plainly-labeled action next to
                                Search itself, visible once a search is
                                actually applied, so it's obvious how to get
                                back to "Recently Searched". */}
                            {hasSearched && (
                                <button
                                    type="button"
                                    onClick={handleClearSearchInput}
                                    aria-label="Clear search"
                                    style={{
                                        background: 'transparent',
                                        border: '1px solid #F6255A',
                                        color: '#F6255A',
                                        borderRadius: '13px',
                                        padding: '0 20px',
                                        height: '44px',
                                        fontWeight: 600,
                                        cursor: 'pointer',
                                        whiteSpace: 'nowrap',
                                    }}
                                >
                                    Clear
                                </button>
                            )}
                        </form>
                    </div>
                </div>

                {/* "Recently Searched" - only while there's no active search
                    to show results for, same as UGC Explorer's own
                    `!selectedApp && <RecentApps .../>` gating. */}
                {!hasSearched && (
                    <RecentApps apps={recentApps} loading={loadingRecentApps} onSelect={handleSelectRecentApp} />
                )}

                {hasSearched &&
                    query.trim() &&
                    !noResults &&
                    Array.isArray(cardsToRender) &&
                    cardsToRender.length > 0 && (
                        <div className="creative-search-summary">
                            <span className="creative-search-summary-main">
                                <span className="creative-search-summary-key">Showing results for</span>{" "}
                                <span className="creative-search-summary-value">"{query.trim()}"</span>
                            </span>
                            {/* Publishers summary (from publisher_name in API) - only for fully verified filter */}
                            {verificationStatusFilter === "fully_verified" && publishersForSummary.length > 0 && (
                                <span className="creative-search-summary-publishers">
                                    {" "}
                                    <span className="creative-search-summary-key">· Publishers:</span>{" "}
                                    <span className="creative-search-summary-value">
                                        {publishersForSummary.join(", ")}
                                    </span>
                                </span>
                            )}
                            {/* Matched domains summary:
                                - Shown when verification status is "All" or "fully_verified"
                                - Hidden when verification status filter is "partial"
                             */}
                            {verificationStatusFilter !== "partial" && matchedDomainsForSummary.length > 0 && (
                                <span className="creative-search-summary-publishers">
                                    {" "}
                                    <span className="creative-search-summary-key">· Matched Domains:</span>{" "}
                                    <span className="creative-search-summary-value">
                                        {matchedDomainsForSummary.map((item, index) => (
                                            <React.Fragment key={index}>
                                                {item.url ? (
                                                    <a
                                                        href={item.url}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        style={{ color: "#ffffff", textDecoration: "none" }}
                                                        onMouseEnter={e => e.target.style.textDecoration = "underline"}
                                                        onMouseLeave={e => e.target.style.textDecoration = "none"}
                                                    >
                                                        {item.name}
                                                    </a>
                                                ) : (
                                                    item.name
                                                )}
                                                {index < matchedDomainsForSummary.length - 1 && ", "}
                                            </React.Fragment>
                                        ))}
                                    </span>
                                </span>
                            )}
                        </div>
                    )}

                {/* Filters Bar */}
                <div className="creative-filters-bar">
                    <div className="filters-row">
                        {/* Basic filters */}
                        <div className="filters-group">
                            <div className="filter-label" style={{ display: "flex", alignItems: "center", gap: 8 }}>
                                <span className="filter-dot filter-dot-basic" />
                                <span>Basic</span>
                            </div>
                            <div className="filter-inputs">
                                {/* Verification status */}
                                <SingleSelectFilter
                                    options={[
                                        { value: "All", label: "All" },
                                        { value: "fully_verified", label: "Fully verified" },
                                        {
                                            value: "partial",
                                            label: "Partial match",
                                            tooltip: "Partial Match refers to ads where the advertiser cannot be fully verified against the searched app publisher, website URL, or associated metadata."
                                        },
                                    ]}
                                    value={verificationStatusFilter}
                                    onChange={handleVerificationStatusChange}
                                    placeholder="Verification status"
                                    formatValue={formatFilterValue}
                                />

                                {/* Active / Inactive */}
                                <SingleSelectFilter
                                    options={[
                                        { value: "All", label: "All" },
                                        { value: "active", label: "Active" },
                                        { value: "inactive", label: "Inactive" },
                                    ]}
                                    value={activeStatusFilter}
                                    onChange={handleActiveStatusChange}
                                    placeholder="Status"
                                    formatValue={formatFilterValue}
                                />

                                {/* Platforms */}
                                <MultiSelectFilter
                                    options={platformOptions}
                                    selected={platformFilter}
                                    onToggle={handlePlatformToggle}
                                    placeholder="Platforms"
                                    formatValue={formatFilterValue}
                                />

                                {/* Template Ad */}
                                <MultiSelectFilter
                                    options={TEMPLATE_AD_OPTIONS}
                                    selected={templateAdFilter}
                                    onToggle={handleTemplateAdToggle}
                                    placeholder="Template Ad"
                                    formatValue={v => (v === "template" ? "Template" : "Regular")}
                                />
                            </div>
                        </div>

                        {/* Creative filters */}
                        <div className="filters-group">
                            <div className="filter-label">
                                <span className="filter-dot filter-dot-creatives" />
                                <span>Creative</span>
                            </div>
                            <div className="filter-inputs">
                                {/* 1) Ad format */}
                                <MultiSelectFilter
                                    options={adFormatOptions}
                                    selected={adFormatFilter}
                                    onToggle={handleAdFormatToggle}
                                    placeholder="Formats"
                                    formatValue={formatFilterValue}
                                />

                                {/* 2) CTA type */}
                                <MultiSelectFilter
                                    options={ctaTypeOptions}
                                    selected={ctaTypeFilter}
                                    onToggle={handleCtaTypeToggle}
                                    placeholder="CTAs"
                                    formatValue={formatFilterValue}
                                />
                            </div>
                        </div>
                    </div>
                </div>

                {/* Applied Filter Badges */}
                {((hasSearched && query.trim()) || (searchInput.trim() && Object.keys(getActiveFilters()).length > 0)) && (
                    <>
                        <div style={{ marginTop: '12px', marginBottom: '6px', display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                            {/* The applied search itself, as its own removable
                                chip - its × is the one that should actually
                                clear the search (searchInput/query/app id),
                                not just narrow it, so "Recently Searched"
                                comes back. Kept separate from the filter
                                chips below, whose × only ever removes that
                                one filter. */}
                            {hasSearched && query.trim() && (
                                <span
                                    style={{
                                        display: 'inline-flex',
                                        alignItems: 'center',
                                        gap: 6,
                                        padding: '4px 8px',
                                        background: 'linear-gradient(90deg, rgba(255,154,184,0.12), rgba(246,37,90,0.12))',
                                        color: '#F6255A',
                                        borderRadius: '6px',
                                        fontSize: '12px',
                                        fontWeight: 500
                                    }}
                                >
                                    <span style={{ marginRight: 4 }}>Search:</span>
                                    <span style={{ opacity: 0.9, color: '#F6255A' }}>{query.trim()}</span>
                                    <button
                                        type="button"
                                        onClick={(e) => {
                                            e.stopPropagation()
                                            e.preventDefault()
                                            handleClearSearchInput()
                                        }}
                                        aria-label="Clear search"
                                        style={{
                                            marginLeft: 4,
                                            background: 'transparent',
                                            border: 'none',
                                            color: '#F6255A',
                                            cursor: 'pointer',
                                            fontSize: 12,
                                            lineHeight: 0.9
                                        }}
                                    >
                                        ×
                                    </button>
                                </span>
                            )}
                            {Object.entries(getActiveFilters()).map(([key, value]) => {
                                if (!value || (Array.isArray(value) && value.length === 0)) return null
                                const displayValue = Array.isArray(value) ? value.join(', ') : value

                                // Professional filter name mapping
                                const filterNameMap = {
                                    "verification_status": "Verification Status",
                                    "status": "Status",
                                    "platform": "Platform",
                                    "format": "Format",
                                    "cta": "CTA Type",
                                    "template_ad": "Template Ad",
                                    "date_range": "Date Range",
                                    "newly_added": "Newly Added"
                                }
                                const filterLabel = filterNameMap[key] || formatFilterValue(key)

                                const isPartialVerification =
                                    key === "verification_status" && displayValue === "Partial Match"

                                return (
                                    <span
                                        key={key}
                                        style={{
                                            display: 'inline-flex',
                                            alignItems: 'center',
                                            gap: 6,
                                            padding: '4px 8px',
                                            background: 'linear-gradient(90deg, rgba(255,154,184,0.12), rgba(246,37,90,0.12))',
                                            color: '#F6255A',
                                            borderRadius: '6px',
                                            fontSize: '12px',
                                            fontWeight: 500
                                        }}
                                    >
                                        <span style={{ marginRight: 4 }}>{filterLabel}:</span>
                                        <span style={{ opacity: 0.9, color: '#F6255A' }}>{displayValue}</span>
                                        <button
                                            type="button"
                                            onClick={(e) => {
                                                e.stopPropagation()
                                                e.preventDefault()
                                                handleRemoveFilter(key)
                                            }}
                                            aria-label={`Remove filter ${key}`}
                                            style={{
                                                marginLeft: 4,
                                                background: 'transparent',
                                                border: 'none',
                                                color: '#F6255A',
                                                cursor: 'pointer',
                                                fontSize: 12,
                                                lineHeight: 0.9
                                            }}
                                        >
                                            ×
                                        </button>
                                    </span>
                                )
                            })}
                        </div>
                        {verificationStatusFilter === "partial" && (
                            <div
                                style={{
                                    marginBottom: '12px',
                                    fontSize: '11px',
                                    color: '#9aa5b2',
                                    fontStyle: 'italic'
                                }}
                            >
                                Note: Showing ads related to the search keyword/game.
                            </div>
                        )}
                    </>
                )}

                {/* Meta Quest / Steam / Apple Vision, matching the shared platform tabs
                    used across the app. Only Meta has ad-creative data today, so
                    Steam, Apple Vision and Pico render disabled with a tooltip rather
                    than being hidden. */}
                <PlatformTabs
                    activeTab={activePlatformTab}
                    onChange={setActivePlatformTab}
                    disabledPlatforms={["steam", "apple_vision", "pico"]}
                    disabledTooltip="Coming soon"
                    variant="pill"
                />

                {/* Date Toolbar (separate from filters section) */}
                <div className="filters-bottom-row">
                    <div className="date-sort-wrapper">
                        <div className="date-range">

                            <button
                                type="button"
                                className={`date-chip ${dateRange === "all" ? "active" : ""}`}
                                onClick={() => handleDateChange("all")}
                            >
                                All
                            </button>
                            <button
                                type="button"
                                className={`date-chip ${dateRange === "today" ? "active" : ""}`}
                                onClick={() => handleDateChange("today")}
                            >
                                Today
                            </button>
                            <button
                                type="button"
                                className={`date-chip ${dateRange === "yesterday" ? "active" : ""}`}
                                onClick={() => handleDateChange("yesterday")}
                            >
                                Yesterday
                            </button>
                            <button
                                type="button"
                                className={`date-chip ${dateRange === "3d" ? "active" : ""}`}
                                onClick={() => handleDateChange("3d")}
                            >
                                3d
                            </button>
                            <button
                                type="button"
                                className={`date-chip ${dateRange === "7d" ? "active" : ""}`}
                                onClick={() => handleDateChange("7d")}
                            >
                                7d
                            </button>
                            <button
                                type="button"
                                className={`date-chip ${dateRange === "30d" ? "active" : ""}`}
                                onClick={() => handleDateChange("30d")}
                            >
                                30d
                            </button>
                            <button
                                type="button"
                                className={`date-chip ${dateRange === "last_year" ? "active" : ""}`}
                                onClick={() => handleDateChange("last_year")}
                            >
                                Last Year
                            </button>
                            <button
                                type="button"
                                className={`date-chip ${dateRange === "custom" ? "active" : ""}`}
                                onClick={handleOpenCustomDateRange}
                            >
                                Customize
                            </button>
                            <label className="newly-added-toggle">
                                <input
                                    type="checkbox"
                                    checked={showNewlyAdded}
                                    onChange={e => handleNewlyAddedChange(e.target.checked)}
                                />
                                <span className="newly-added-label">Newly added</span>
                            </label>
                        </div>
                    </div>
                </div>

                {/* Cards Grid */}
                <div className="creative-grid-wrapper">
                    {!loading && !filtering && error && <p className="creative-status-text error">{error}</p>}
                    {!loading &&
                        !filtering &&
                        !error &&
                        !hasSearched && (
                            <div className="creative-empty-state">
                                <div className="creative-empty-state-text">
                                    Start by searching for an app or game above to see its ad creatives.
                                </div>
                            </div>
                        )}
                    {!loading &&
                        !filtering &&
                        !error &&
                        hasSearched &&
                        noResults && (
                            <div className="creative-empty-state">
                                <div className="creative-empty-state-text">
                                    No ads found for &quot;{query}&quot;.
                                </div>
                            </div>
                        )}
                    {!loading &&
                        !filtering &&
                        !error &&
                        shouldShowFilterEmptyState && (
                            <div className="creative-empty-state">
                                <div className="creative-empty-state-text">
                                    No ads match your current filters. Try choosing another filter — showing similar
                                    results instead.
                                </div>
                            </div>
                        )}
                    {!loading &&
                        !filtering &&
                        !error &&
                        shouldShowFilterEmptyState && cardsToRender.length > 0 && (
                            <div style={{ marginTop: 16, marginBottom: 4, color: "rgba(232,232,232,0.9)", fontSize: 14, fontWeight: 500 }}>
                                Similar ads
                            </div>
                        )}
                    <Row className="g-3">
                        {loading || filtering ? (
                            // Show skeleton cards while loading or filtering
                            Array.from({ length: 8 }).map((_, index) => (
                                <Col key={`skeleton-${index}`} xs={12} sm={6} md={4} lg={3}>
                                    <div className="creative-card">
                                        <div className="creative-card-media">
                                            <Skeleton
                                                height="100%"
                                                borderRadius={0}
                                                baseColor="#171821"
                                                highlightColor="#2a2b37"
                                                enableAnimation={true}
                                            />
                                        </div>
                                        <div className="creative-card-body">
                                            <Skeleton
                                                height={20}
                                                width="80%"
                                                baseColor="#21222D"
                                                highlightColor="#2a2b37"
                                                enableAnimation={true}
                                                style={{ marginBottom: 8 }}
                                            />
                                            <Skeleton
                                                height={16}
                                                width="60%"
                                                baseColor="#21222D"
                                                highlightColor="#2a2b37"
                                                enableAnimation={true}
                                            />
                                        </div>
                                    </div>
                                </Col>
                            ))
                        ) : (
                            cardsToRender.map(ad => {
                                const hasVideo = Array.isArray(ad.video_urls) && ad.video_urls.length > 0
                                const hasImage = Array.isArray(ad.image_urls) && ad.image_urls.length > 0
                                const primaryVideo = hasVideo ? ad.video_urls[0] : null
                                const primaryImage = hasImage ? ad.image_urls[0] : "https://via.placeholder.com/320x180.png?text=No+Image"

                                const title = ad.matched_game_name || ad.page_name || ad.body || "Untitled ad"

                                const subtitleParts = []
                                if (Array.isArray(ad.platforms) && ad.platforms.length > 0) {
                                    // Instead of listing platforms, show a friendly message
                                    subtitleParts.push("Live Campaigns on these platform..")
                                }
                                const subtitle =
                                    subtitleParts.join(" • ") || ad.description || ad.query || ""

                                const durationLabel =
                                    ad.started_running || (ad.is_active ? "Active" : "") || ""

                                // View Ad button redirects to the ad's canonical Ads Library
                                // link — rebuilt from whatever archive id we can find, since
                                // ad_snapshot_url as returned isn't always the direct single-ad
                                // link Meta expects (see buildAdLibraryUrl for why).
                                const viewAdUrl = buildAdLibraryUrl(ad)

                                // Link below redirects to link_url
                                const linkUrl = ad.link_url || null

                                // For the visible URL at the bottom of the card, use link_url
                                const rawDisplayUrl = ad.link_url || null
                                const displayUrl = rawDisplayUrl
                                    ? (rawDisplayUrl || "")
                                        .replace(/^https?:\/\//i, "")
                                        .split("?")[0]
                                    : null

                                const platforms = Array.isArray(ad.platforms) ? ad.platforms : []

                                // Get verification status (handle both API format and filter format)
                                const verificationStatus = ad.verification_status
                                const isFullyVerified = verificationStatus === "fully_verified" || verificationStatus === "FULLY_VERIFIED"
                                const isPartial = verificationStatus === "partial" || verificationStatus === "PARTIAL_MATCH"

                                // Get domain for display (only show when fully verified)
                                const matchedDomain = ad.page_name || null

                                return (
                                    <Col key={ad._id || ad.ad_id} xs={12} sm={6} md={4} lg={3}>
                                        <div className="creative-card">
                                            <div className="creative-card-media">
                                                {durationLabel && (
                                                    <span className="creative-duration">{durationLabel}</span>
                                                )}
                                                {hasVideo ? (
                                                    <>
                                                        <button
                                                            type="button"
                                                            className="creative-play-btn"
                                                            aria-label="Toggle video playback"
                                                            onClick={e => {
                                                                e.stopPropagation()
                                                                const video = e.currentTarget.parentElement.querySelector("video")
                                                                if (video) {
                                                                    if (video.paused) {
                                                                        video.play()
                                                                    } else {
                                                                        video.pause()
                                                                    }
                                                                }
                                                            }}
                                                        >
                                                            ▶
                                                        </button>
                                                        <video
                                                            src={primaryVideo}
                                                            controls={false}
                                                            muted
                                                            loop
                                                            playsInline
                                                            autoPlay
                                                            style={{
                                                                width: "100%",
                                                                height: "100%",
                                                                objectFit: "cover",
                                                                display: "block",
                                                            }}
                                                        />
                                                    </>
                                                ) : (
                                                    <img 
                                                        src={primaryImage} 
                                                        onError={(e) => {
                                                            e.target.src = fallbackLogo;
                                                            e.target.style.objectFit = 'contain';
                                                            e.target.style.padding = '10px';
                                                        }}
                                                    />
                                                )}
                                            </div>
                                            <div className="creative-card-body">
                                                <h2 className="creative-card-title">
                                                    {title}
                                                </h2>
                                                <p className="creative-card-subtitle">{subtitle}</p>

                                                {platforms.length > 0 && (
                                                    <div className="creative-card-platforms">
                                                        {platforms.map(p => {
                                                            const key = `${ad._id || ad.ad_id}-${p}`
                                                            const normalized = typeof p === "string" ? p.toLowerCase() : ""
                                                            return (
                                                                <div
                                                                    key={key}
                                                                    className={`creative-platform-pill creative-platform-${normalized}`}
                                                                >
                                                                    {normalized === "facebook" && (
                                                                        <i className="fab fa-facebook-f" aria-label="Facebook" />
                                                                    )}
                                                                    {normalized === "instagram" && (
                                                                        <i className="fab fa-instagram" aria-label="Instagram" />
                                                                    )}
                                                                    {normalized === "messenger" && (
                                                                        <i className="fab fa-facebook-messenger" aria-label="Messenger" />
                                                                    )}
                                                                    {normalized === "audience_network" && "A"}
                                                                    {normalized === "threads" && "T"}
                                                                    {!["facebook", "instagram", "messenger", "audience_network", "threads"].includes(
                                                                        normalized
                                                                    ) &&
                                                                        (p || "").charAt(0)}
                                                                </div>
                                                            )
                                                        })}
                                                    </div>
                                                )}

                                                {viewAdUrl && (
                                                    <button
                                                        type="button"
                                                        className="creative-card-cta-btn"
                                                        onClick={() => {
                                                            trackEvent(ANALYTICS_EVENTS.FEATURE.AD_DISCOVERY_AD_OPENED, {
                                                                ad_id: ad._id || ad.ad_id,
                                                                app_name: ad.matched_game_name,
                                                                link_type: "view_ad",
                                                            })
                                                            window.open(viewAdUrl, "_blank", "noopener,noreferrer")
                                                        }}
                                                    >
                                                        View Ad
                                                    </button>
                                                )}

                                                {displayUrl && (
                                                    <div
                                                        className="creative-card-link-row"
                                                        onClick={() => {
                                                            if (linkUrl) {
                                                                trackEvent(ANALYTICS_EVENTS.FEATURE.AD_DISCOVERY_AD_OPENED, {
                                                                    ad_id: ad._id || ad.ad_id,
                                                                    app_name: ad.matched_game_name,
                                                                    link_type: "landing_page",
                                                                })
                                                                window.open(
                                                                    linkUrl,
                                                                    "_blank",
                                                                    "noopener,noreferrer"
                                                                )
                                                            }
                                                        }}
                                                        style={{ cursor: linkUrl ? "pointer" : "default" }}
                                                    >
                                                        <span className="creative-card-link-icon">🔗</span>
                                                        <span
                                                            className="creative-card-link-text"
                                                            title={displayUrl}
                                                        >
                                                            {displayUrl}
                                                        </span>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    </Col>
                                )
                            })
                        )}
                    </Row>
                    {shouldShowPagination && (
                        <div
                            style={{
                                display: "flex",
                                justifyContent: "center",
                                gap: 6,
                                marginTop: 16,
                                flexWrap: "wrap",
                            }}
                        >
                            <button
                                type="button"
                                onClick={handlePrevPage}
                                disabled={page <= 1}
                                style={{
                                    background: "transparent",
                                    border: "1px solid rgba(255,255,255,0.06)",
                                    color: page <= 1 ? "rgba(255,255,255,0.35)" : "#fff",
                                    padding: "4px 8px",
                                    borderRadius: 4,
                                    fontSize: "12px",
                                    cursor: page <= 1 ? "not-allowed" : "pointer",
                                }}
                            >
                                Prev
                            </button>

                            {getPageNumbers(page, totalPages).map((p, index) => {
                                if (p === "...") {
                                    return (
                                        <span
                                            key={`ad-disc-ellipsis-${index}`}
                                            style={{
                                                minWidth: 28,
                                                height: 28,
                                                display: "flex",
                                                alignItems: "center",
                                                justifyContent: "center",
                                                color: "rgba(255,255,255,0.8)",
                                                fontWeight: 600,
                                                fontSize: "12px",
                                            }}
                                        >
                                            ...
                                        </span>
                                    )
                                }
                                return (
                                    <button
                                        key={`ad-disc-page-${p}`}
                                        type="button"
                                        onClick={() => {
                                            const next = Math.min(Math.max(1, p), totalPages)
                                            setPage(next)
                                            trackEvent(ANALYTICS_EVENTS.FEATURE.AD_DISCOVERY_PAGINATED, { page: next })
                                        }}
                                        disabled={loading}
                                        style={{
                                            minWidth: 28,
                                            height: 28,
                                            borderRadius: 4,
                                            border: "1px solid rgba(255,255,255,0.06)",
                                            background: page === p ? "#ff2f6d" : "transparent",
                                            color: page === p ? "#fff" : "rgba(255,255,255,0.8)",
                                            fontWeight: 600,
                                            fontSize: "12px",
                                            cursor: "pointer",
                                        }}
                                    >
                                        {p}
                                    </button>
                                )
                            })}

                            <button
                                type="button"
                                onClick={handleNextPage}
                                disabled={page >= totalPages}
                                style={{
                                    background: "transparent",
                                    border: "1px solid rgba(255,255,255,0.06)",
                                    color:
                                        page >= totalPages ? "rgba(255,255,255,0.35)" : "#fff",
                                    padding: "4px 8px",
                                    borderRadius: 4,
                                    fontSize: "12px",
                                    cursor:
                                        page >= totalPages ? "not-allowed" : "pointer",
                                }}
                            >
                                Next
                            </button>
                        </div>
                    )}
                </div>
            </Container>
        </div>
    )
}

export default CreativeInsightsDashboard

